import styled from '@emotion/styled';
import { CircularProgress, Popover, Tooltip } from '@mui/material';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { twMerge } from 'tailwind-merge';
import tw from 'twin.macro';

import DownloadIcon from '../../../assets/svg/DownloadIcon';
import { USER_ROLE } from '../../../constants';
import { downloadFile } from '../../../helpers';
import { useAuthContext, useSalesforceContext } from '../../../hooks';

export const StyledCircularProgress = styled(CircularProgress)`
  ${tw`text-brand-300`}
`;

export const DownloadSentEmailDetails = ({ filters }) => {
  const { userRole } = useAuthContext();
  const { isSalesforceAvailable, isSendToSalesforceLoading, sendToSalesforce } =
    useSalesforceContext();

  const isAdmin = userRole === USER_ROLE.ADMIN;
  const isManager = userRole === USER_ROLE.MANAGER;
  const isCaller = userRole === USER_ROLE.CALLER;

  const [isPending, setIsPending] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);

  const onOpenPopover = e => {
    setAnchorEl(e.currentTarget);
  };

  const onClosePopover = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);
  const isSalesforceDisabled = !isSalesforceAvailable || !filters.startDate;

  const requestBody = {
    startDate: filters.startDate,
    endDate: filters.endDate,
    ...(!!filters.adminTeam && isAdmin && { adminTeam: filters.adminTeam }),
    ...(!!filters.callerId && isManager && { callerId: filters.callerId }),
  };

  const download = async () => {
    setIsPending(true);

    try {
      const response = await fetch(
        isAdmin
          ? '/admin/export-email-outcome-report'
          : '/export-email-outcome-report',
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(requestBody),
        },
      );

      if (response.status !== 200) throw new Error();

      const blob = await response.blob();
      downloadFile(blob, 'Send Email Outcome.csv');
      setIsPending(false);
    } catch {
      setIsPending(false);
      toast.error('Something went wrong, please try again!', {
        position: 'top-right',
        autoClose: 3000,
      });
    }
  };

  return (
    <>
      {isPending || isSendToSalesforceLoading ? (
        <StyledCircularProgress size={18} />
      ) : (
        <Tooltip title="Download" placement="top">
          <button
            onClick={isCaller ? download : onOpenPopover}
            className="w-[18px] h-[18px] rounded-full bg-brand-300/20 outline-none flex justify-center items-center disabled:bg-transparent"
          >
            <DownloadIcon className="w-3 h-3 text-brand-500" />
          </button>
        </Tooltip>
      )}

      <Popover
        open={open}
        slotProps={{
          paper: {
            className:
              '!rounded-lg mt-2 !shadow-[0_4px_20px_0_rgba(17,17,58,0.08)] bg-white w-40',
          },
        }}
        anchorEl={anchorEl}
        onClose={onClosePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
      >
        <div className="p-0.5 flex flex-col gap-0.5">
          <div
            className="px-2 py-1.5 cursor-pointer rounded-lg hover:bg-brand-300/[0.08]"
            onClick={() => {
              download();
              onClosePopover();
            }}
          >
            <p className="text-[14px]/[20px] text-base-1000 font-medium">CSV</p>
          </div>
          <div
            className={twMerge(
              'px-2 py-1.5',
              !isSalesforceDisabled &&
                'cursor-pointer rounded-lg hover:bg-brand-300/[0.08]',
            )}
            onClick={() => {
              if (isSalesforceDisabled) return;

              sendToSalesforce({
                body: { reportType: 'email-outcomes', ...requestBody },
              });
              onClosePopover();
            }}
          >
            <p
              className={twMerge(
                'text-[14px]/[20px] font-medium',
                isSalesforceDisabled ? 'text-base-400' : 'text-base-1000',
              )}
            >
              Send to Salesforce
            </p>
          </div>
        </div>
      </Popover>
    </>
  );
};
